import { Controller } from "@hotwired/stimulus";
import SlickGridTable from "../../../../../../app/javascript/sgTable";

import * as utilsFormatter from "../../../../../../app/javascript/utils/formatters";

const computeGridHeight = ({ visibleRows, allRows, rowCount }) => {
  const tblHeaderHeight = 40;
  const scrollBarHeight = 12;
  const rowHeight = 32;
  
  const rows = allRows 
    ? Math.max(0, rowCount | 0)
    : Math.max(0, visibleRows | 0);
  
  return tblHeaderHeight + rows * rowHeight + scrollBarHeight;
};

export default class extends Controller {
  static targets = ["table", "search", "settings", "settingsModal"];
  static values = {
    data: Array,
    columnDefinitions: Array,
    tableId: String,
    defaultSort: Object,
    tableOptions: Object,
    quoteId: Number,
    updateSettingsUrl: String
  };

  async connect() {
    await this.loadSettingsFromDatabase();
    this.initializeTable();
    this.setupEventListeners();
  }

  disconnect() {
    if (this.slickGridTable) {
      this.slickGridTable = null;
    }
    if (this.grid) {
      this.grid = null;
    }
  }

  async loadSettingsFromDatabase() {
    if (!this.updateSettingsUrlValue) {
      return;
    }

    try {
      const response = await fetch(this.updateSettingsUrlValue, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.display_settings) {
          this.mergeDatabaseSettingsWithLocalStorage(result.display_settings);
        }
      }
    } catch (error) {
      console.error('Failed to load settings from database:', error);
    }
  }

  mergeDatabaseSettingsWithLocalStorage(dbSettings) {
    const route = window.location.pathname;
    const stored = localStorage.getItem('omsTableSettings');
    const settings = stored ? JSON.parse(stored) : {};
    
    if (!settings[route]) {
      settings[route] = {};
    }

    if (dbSettings.columns) {
      settings[route][`${this.tableIdValue}_columns`] = dbSettings.columns;
    }
    if (dbSettings.columnOrder) {
      settings[route][`${this.tableIdValue}_columnOrder`] = dbSettings.columnOrder;
    }
    if (dbSettings.sortOrder) {
      // Ensure sortOrder is stored as an array for consistency
      const sortOrder = Array.isArray(dbSettings.sortOrder) ? dbSettings.sortOrder : 
                        (dbSettings.sortOrder.column ? [dbSettings.sortOrder] : []);
      settings[route][`${this.tableIdValue}_sortOrder`] = sortOrder;
    }

    localStorage.setItem('omsTableSettings', JSON.stringify(settings));
  }

  async saveSettingsToDatabase(routeSettings = null) {
    if (!this.updateSettingsUrlValue) {
      return;
    }

    try {
      if (!routeSettings) {
        const route = window.location.pathname;
        const stored = localStorage.getItem('omsTableSettings');
        const settings = stored ? JSON.parse(stored) : {};
        routeSettings = settings[route] || {};
      }

      const savedSortOrder = routeSettings[`${this.tableIdValue}_sortOrder`];
      let sortOrderForDb = [];
      
      // Handle conversion of sort order for database storage
      if (savedSortOrder) {
        if (Array.isArray(savedSortOrder)) {
          sortOrderForDb = savedSortOrder;
        } else if (savedSortOrder.column) {
          // Convert legacy single-column format to array
          sortOrderForDb = [savedSortOrder];
        }
      } else if (this.defaultSortValue) {
        sortOrderForDb = [this.defaultSortValue];
      }

      const displaySettings = {
        columns: routeSettings[`${this.tableIdValue}_columns`] || this.columnDefinitionsValue.map(col => col.id),
        columnOrder: routeSettings[`${this.tableIdValue}_columnOrder`] || this.columnDefinitionsValue.map(col => col.id),
        sortOrder: sortOrderForDb
      };

      const response = await fetch(this.updateSettingsUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          display_settings: displaySettings
        })
      });
    } catch (error) {
      console.error('Failed to save settings to database:', error);
    }
  }

  initializeTable() {
    const columns = this.processColumnDefinitions();
    const visibleColumns = this.getVisibleColumns();
    let visibleColumnDefs = columns.filter(col => visibleColumns.includes(col.id));

    const savedSortOrder = this.getSavedSortOrder();
    const initialSort = savedSortOrder || (this.defaultSortValue ? [this.defaultSortValue] : []);

    // Apply saved column order if it exists
    const savedColumnOrder = this.getSavedColumnOrder();
    if (savedColumnOrder && savedColumnOrder.length > 0) {
      // Reorder visibleColumnDefs based on saved order
      const orderedColumns = [];
      savedColumnOrder.forEach(colId => {
        const col = visibleColumnDefs.find(c => c.id === colId);
        if (col) {
          orderedColumns.push(col);
        }
      });
      // Add any remaining columns that weren't in the saved order
      visibleColumnDefs.forEach(col => {
        if (!savedColumnOrder.includes(col.id)) {
          orderedColumns.push(col);
        }
      });
      visibleColumnDefs = orderedColumns;
    }

    const { dataKey: selectedRowDataKey, idsKey: selectedRowsKey } = this.getSelectionStorageKeys();

    this.slickGridTable = new SlickGridTable({
      data: this.dataValue,
      columnDefinitions: visibleColumnDefs,
      sortDefault: initialSort.length > 0 ? initialSort : (this.defaultSortValue ? [this.defaultSortValue] : []),
      selectedColumns: visibleColumns,
      tableTarget: this.tableIdValue,
      showCheckbox: true,
      showTotals: true,
      totalsOptions: {
        fields: ["par_value", "gross_consideration"],
        label: "Totals",
        cssClasses: ""
      },
      options: {
        frozenColumn: 1, // Pin the first column to the left
        editable: false,
        enableAddRow: false,
        enableCellNavigation: true,
        leaveSpaceForNewRows: false,
        alwaysShowVerticalScroll: true,
        asyncEditorLoading: true,
        autoEdit: false,
        autoHeight: false,
        enableAsyncPostRender: true,
        syncColumnCellResize: true,
        rowHeight: 32,
        enableAutoResize: false,
        ...this.tableOptionsValue,
        selectedRowDataKey,
        selectedRowsKey
      }
    });

    this.grid = this.slickGridTable.grid;
    const h = computeGridHeight({ 
      visibleRows: this.dataValue.length,
      allRows: true,
      rowCount: this.grid.getDataLength()
    });
    const container = document.getElementById(this.tableIdValue);
    container.style.height = `${h}px`;
    this.grid.resizeCanvas();

    this.bindGridEvents();

    if (!this.slickGridTable._bindEventsPatched) {
      const originalRender = this.slickGridTable.renderTable.bind(this.slickGridTable);
      this.slickGridTable.renderTable = (...args) => {
        const result = originalRender(...args);
        this.grid = this.slickGridTable.grid;
        this.bindGridEvents();
        return result;
      };
      this.slickGridTable._bindEventsPatched = true;
    }
  }

  processColumnDefinitions() {
    return this.columnDefinitionsValue.map(col => {
      const newColumn = { ...col };

      newColumn.width = col.width || 120;
      newColumn.sortable = col.sortable !== false;
      newColumn.resizable = col.resizable !== false;
      newColumn.reorderable = col.reorderable !== false;
      newColumn.field = col.field || col.id;

      if (newColumn.id === "isin") {
        newColumn.formatter = utilsFormatter.isinFormatter;
      }
      else if (newColumn.formatter && typeof newColumn.formatter === "string") {
        switch (newColumn.formatter) {
          case "integer":
            newColumn.formatter = utilsFormatter.integerFormatter;
            break;
          case "currency":
            newColumn.formatter = utilsFormatter.currencyFormatter;
            break;
          case "currency3":
            newColumn.formatter = utilsFormatter.currency3Formatter;
            break;
          case "decimal0":
            newColumn.formatter = utilsFormatter.decimal0Formatter;
            break;
          case "decimal1":
            newColumn.formatter = utilsFormatter.decimal1Formatter;
            break;
          case "decimal2":
            newColumn.formatter = utilsFormatter.decimal2Formatter;
            break;
          case "decimal3":
            newColumn.formatter = utilsFormatter.decimal3Formatter;
            break;
          case "percent1":
            newColumn.formatter = utilsFormatter.percent1Formatter;
            break;
          case "percent2":
            newColumn.formatter = utilsFormatter.percent2Formatter;
            break;
          case "percent3":
            newColumn.formatter = utilsFormatter.percent3Formatter;
            break;
          case "date":
            newColumn.formatter = utilsFormatter.dateFormatter;
            break;
          case "volume500k":
            newColumn.formatter = utilsFormatter.volume500kFormatter;
            break;
          case "link":
            newColumn.formatter = utilsFormatter.simpleLinkFormatter;
            break;
          default:
            // If formatter string doesn't match, remove it to avoid errors
            delete newColumn.formatter;
        }
      }

      newColumn.cssClass = newColumn.cssClass ? `${newColumn.cssClass} ba-slick-cell` : "ba-slick-cell";

      return newColumn;
    });
  }

  getVisibleColumns() {
    const route = window.location.pathname;
    const stored = localStorage.getItem('omsTableSettings');
    if (stored) {
      const settings = JSON.parse(stored);
      const routeSettings = settings[route] || {};
      const storedColumns = routeSettings[`${this.tableIdValue}_columns`];
      
      if (storedColumns) {
        return storedColumns.filter(colId => 
          this.columnDefinitionsValue.some(col => col.id === colId)
        );
      }
    }
    return this.columnDefinitionsValue.map(col => col.id);
  }

  getSavedColumnOrder() {
    const route = window.location.pathname;
    const stored = localStorage.getItem('omsTableSettings');
    if (stored) {
      const settings = JSON.parse(stored);
      const routeSettings = settings[route] || {};
      return routeSettings[`${this.tableIdValue}_columnOrder`];
    }
    return null;
  }

  getSavedSortOrder() {
    const route = window.location.pathname;
    const stored = localStorage.getItem('omsTableSettings');
    if (stored) {
      const settings = JSON.parse(stored);
      const routeSettings = settings[route] || {};
      const sort = routeSettings[`${this.tableIdValue}_sortOrder`];
      
      if (sort) {
        if (Array.isArray(sort) && sort.length > 0) {
          return sort;
        } else if (sort.column) {
          return [sort]; 
        }
      }
    }
    return null;
  }

  getSelectionStorageKeys() {
    const route = window.location.pathname;
    const stored = localStorage.getItem('omsTableSettings');
    const settings = stored ? JSON.parse(stored) : {};
    const routeSettings = settings[route] || {};

    let dataKey = routeSettings[`${this.tableIdValue}_selectedRowDataKey`];
    let idsKey = routeSettings[`${this.tableIdValue}_selectedRowsKey`];

    if (!dataKey || !idsKey) {
      dataKey = `oms:${route}:${this.tableIdValue}:selectedRowData`;
      idsKey = `oms:${route}:${this.tableIdValue}:selectedRows`;
      this.saveTableSettings(`${this.tableIdValue}_selectedRowDataKey`, dataKey);
      this.saveTableSettings(`${this.tableIdValue}_selectedRowsKey`, idsKey);
    }

    return { dataKey, idsKey };
  }

  setupEventListeners() {
    document.addEventListener('click', (e) => {
      if (this.hasSettingsTarget && !this.settingsTarget.contains(e.target) && 
          !e.target.closest('[data-action*="toggleSettings"]')) {
        this.hideSettings();
      }
    });
  }

  toggleColumn(event) {
    const columnId = event.target.value;
    const visibleColumns = this.getVisibleColumns();
    const index = visibleColumns.indexOf(columnId);
    
    if (index > -1) {
      visibleColumns.splice(index, 1);
    } else {
      visibleColumns.push(columnId);
    }
    
    this.saveTableSettings(`${this.tableIdValue}_columns`, visibleColumns);
    this.renderTable();
  }

  resetColumns() {
    this.saveTableSettings(`${this.tableIdValue}_columns`, null);
    this.renderTable();
  }

  saveTableSettings(key, value) {
    const route = window.location.pathname;
    const stored = localStorage.getItem('omsTableSettings');
    const settings = stored ? JSON.parse(stored) : {};
    
    if (!settings[route]) {
      settings[route] = {};
    }
    
    if (value === null) {
      delete settings[route][key];
    } else {
      settings[route][key] = value;
    }
    
    localStorage.setItem('omsTableSettings', JSON.stringify(settings));
    
    if (this.updateSettingsUrlValue) {
      this.saveSettingsToDatabase(settings[route]);
    }
  }

  toggleSettings() {
    if (this.settingsTarget.classList.contains('hidden')) {
      this.showSettings();
    } else {
      this.hideSettings();
    }
  }

  showSettings() {
    this.settingsTarget.classList.remove('hidden');
    const visibleColumns = this.getVisibleColumns();
    this.settingsTarget.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = visibleColumns.includes(checkbox.value);
    });
  }

  hideSettings() {
    this.settingsTarget.classList.add('hidden');
  }

  showSettingsModal() {
    const modal = document.getElementById(`oms-${this.tableIdValue}-settings-modal`);
    if (!modal) return;
    this.pendingVisibleColumns = [...this.getVisibleColumns()];
    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    const visibleColumns = this.getVisibleColumns();
    modal.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = visibleColumns.includes(checkbox.value);
    });
  }

  hideSettingsModal() {
    const modal = document.getElementById(`oms-${this.tableIdValue}-settings-modal`);
    if (!modal) return;
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
  }

  toggleColumnInModal(event) {
    if (!this.pendingVisibleColumns) {
      this.pendingVisibleColumns = [...this.getVisibleColumns()];
    }
    const columnId = event.target.value;
    const existsIndex = this.pendingVisibleColumns.indexOf(columnId);
    if (event.target.checked) {
      if (existsIndex === -1) this.pendingVisibleColumns.push(columnId);
    } else {
      if (existsIndex > -1) this.pendingVisibleColumns.splice(existsIndex, 1);
    }
  }

  resetColumnsInModal() {
    this.pendingVisibleColumns = this.columnDefinitionsValue.map(col => col.id);
    const modal = document.getElementById(`oms-${this.tableIdValue}-settings-modal`);
    if (modal) {
      modal.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        checkbox.checked = this.pendingVisibleColumns.includes(checkbox.value);
      });
    }
  }

  submitSettingsModal() {
    if (!this.pendingVisibleColumns) {
      this.pendingVisibleColumns = [...this.getVisibleColumns()];
    }
    this.saveTableSettings(`${this.tableIdValue}_columns`, this.pendingVisibleColumns);
    this.renderTable();
    this.hideSettingsModal();
  }

  onSearch(event) {
    const searchTerm = event.target.value;
    if (this.slickGridTable) {
      this.slickGridTable.onSearch(searchTerm);
    }
  }

  onColumnReorder(event, args) {
    const { impactedColumns: columns } = args;
    const columnOrder = columns.map(col => col.id);
    this.saveTableSettings(`${this.tableIdValue}_columnOrder`, columnOrder);
  }

  onSort(event, args) {
    let sortOrders = [];
    
    if (args.multiColumnSort && args.sortCols) {
      // Multi-column sorting
      sortOrders = args.sortCols.map(sortCol => ({
        column: sortCol.sortCol.field,
        direction: sortCol.sortAsc ? 'asc' : 'desc'
      }));
    } else if (args.sortCol) {
      sortOrders = [{
        column: args.sortCol.field,
        direction: args.sortAsc ? 'asc' : 'desc'
      }];
    }
    
    this.saveTableSettings(`${this.tableIdValue}_sortOrder`, sortOrders);
  }

  bindGridEvents() {
    if (!this.grid) return;

    if (this._sortBoundGrid !== this.grid && this.grid.onSort) {
      this.grid.onSort.subscribe((e, args) => this.onSort(e, args));
      this._sortBoundGrid = this.grid;
    }

    if (this._reorderBoundGrid !== this.grid && this.grid.onColumnsReordered) {
      this.grid.onColumnsReordered.subscribe((e, args) => this.onColumnReorder(e, args));
      this._reorderBoundGrid = this.grid;
    }
  }

  renderTable() {
    if (this.slickGridTable) {
      this.initializeTable();
    }
  }
}
